<template>
  <section class="featured-products">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">Featured Products</h2>
        <p class="section-description">
          Explore our range of high-performance ceramic capacitors engineered 
          for reliability and precision in critical applications.
        </p>
      </div>
      
      <div class="products-grid">
        <div 
          v-for="product in featuredProducts" 
          :key="product.id"
          class="product-card"
          :class="{ 'product-large': product.featured }"
        >
          <div class="product-image">
            <div class="product-overlay">
              <div class="product-category">{{ product.category }}</div>
              <h3 class="product-title">{{ product.title }}</h3>
              <p class="product-description">{{ product.description }}</p>
              <div class="product-specs">
                <span class="spec">{{ product.voltageRange }}</span>
                <span class="spec">{{ product.capacitanceRange }}</span>
              </div>
            </div>
            <div class="product-placeholder">
              <span class="product-icon">{{ product.icon }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div class="products-cta">
        <RouterLink to="/products" class="btn btn-primary">
          View All Products
        </RouterLink>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { RouterLink } from 'vue-router'

interface Product {
  id: number
  title: string
  description: string
  category: string
  voltageRange: string
  capacitanceRange: string
  icon: string
  featured?: boolean
}

const featuredProducts = ref<Product[]>([
  {
    id: 1,
    title: 'MLCC Series X7R',
    description: 'High-capacity multilayer ceramic capacitors with stable temperature characteristics for general purpose applications.',
    category: 'Multilayer Ceramic',
    voltageRange: '6.3V - 100V',
    capacitanceRange: '1pF - 100µF',
    icon: '📦',
    featured: true
  },
  {
    id: 2,
    title: 'High Voltage Class 2',
    description: 'Designed for power supply filtering and energy storage in demanding applications.',
    category: 'High Voltage',
    voltageRange: '250V - 3kV',
    capacitanceRange: '10pF - 10µF',
    icon: '⚡'
  },
  {
    id: 3,
    title: 'RF/Microwave Series',
    description: 'Ultra-low ESR capacitors optimized for high-frequency circuits and RF applications.',
    category: 'RF & Microwave',
    voltageRange: '16V - 100V',
    capacitanceRange: '0.5pF - 1µF',
    icon: '📡'
  },
  {
    id: 4,
    title: 'Automotive AEC-Q200',
    description: 'Safety-certified capacitors meeting stringent automotive reliability standards.',
    category: 'Automotive Grade',
    voltageRange: '10V - 250V',
    capacitanceRange: '10pF - 47µF',
    icon: '🚗'
  }
])
</script>

<style scoped>
.featured-products {
  padding: 6rem 0;
  background: var(--color-background-soft);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.section-header {
  text-align: center;
  max-width: 600px;
  margin: 0 auto 4rem;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  color: var(--color-heading);
}

.section-description {
  font-size: 1.2rem;
  line-height: 1.6;
  color: var(--color-text);
  margin: 0;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.product-large {
  grid-column: span 2;
}

.product-card {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  transition: all 0.3s ease;
  min-height: 300px;
}

.product-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.product-image {
  position: relative;
  height: 100%;
  min-height: 300px;
  overflow: hidden;
}

.product-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.9;
}

.product-icon {
  font-size: 4rem;
}

.product-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.3) 0%,
    rgba(0, 0, 0, 0.5) 50%,
    rgba(0, 0, 0, 0.8) 100%
  );
  color: white;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  z-index: 2;
}

.product-category {
  background: var(--color-primary);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  width: fit-content;
  margin-bottom: 1rem;
}

.product-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: white;
}

.product-large .product-title {
  font-size: 2rem;
}

.product-description {
  font-size: 1rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 1rem 0;
}

.product-large .product-description {
  font-size: 1.1rem;
}

.product-specs {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.spec {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  backdrop-filter: blur(10px);
}

.products-cta {
  text-align: center;
}

.btn {
  display: inline-flex;
  align-items: center;
  padding: 1rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.btn-primary {
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(244, 196, 48, 0.4);
}

@media (max-width: 768px) {
  .featured-products {
    padding: 4rem 0;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .products-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .product-large {
    grid-column: span 1;
  }
  
  .product-overlay {
    padding: 1.5rem;
  }
  
  .product-title {
    font-size: 1.25rem;
  }
  
  .product-large .product-title {
    font-size: 1.5rem;
  }
}
</style>
